import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
  AttachMoney as CurrencyIcon,
  Schedule as TimezoneIcon,
  CalendarMonth as FiscalYearIcon,
  LocationOn as AddressIcon,
  ContactPhone as ContactIcon,
  Badge as RegistrationIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import api from '../../config/api';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const CompanySettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [timezones, setTimezones] = useState([]);

  const [settings, setSettings] = useState({
    name: '',
    legalName: '',
    logoUrl: '',
    registrationNumber: '',
    taxId: '',
    country: '',
    registeredAddress: {
      street: '',
      city: '',
      state: '',
      postcode: '',
      country: ''
    },
    contactEmail: '',
    contactPhone: '',
    defaultCurrency: 'USD',
    timezone: 'UTC',
    fiscalYearStart: 1,
    inventorySource: 'internal'
  });

  useEffect(() => {
    fetchSettings();
    fetchCurrencies();
    fetchTimezones();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/company-settings');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching company settings:', err);
      setError(err.response?.data?.error || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await api.get('/api/company-settings/currencies');
      if (response.data.success) {
        setCurrencies(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching currencies:', err);
    }
  };

  const fetchTimezones = async () => {
    try {
      const response = await api.get('/api/company-settings/timezones');
      if (response.data.success) {
        setTimezones(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching timezones:', err);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      registeredAddress: {
        ...prev.registeredAddress,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await api.put('/api/company-settings', settings);

      if (response.data.success) {
        setSuccess('Company settings saved successfully');
        setSettings(response.data.data);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error saving company settings:', err);
      setError(err.response?.data?.error || 'Failed to save company settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Company Profile */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Company Profile</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={settings.logoUrl}
                  sx={{ width: 80, height: 80, mr: 2 }}
                >
                  {settings.name?.charAt(0) || 'C'}
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Company Logo
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Logo URL"
                    value={settings.logoUrl || ''}
                    onChange={(e) => handleChange('logoUrl', e.target.value)}
                    sx={{ width: 250 }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Company Name"
                value={settings.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Legal Name"
                placeholder="Registered legal name"
                value={settings.legalName || ''}
                onChange={(e) => handleChange('legalName', e.target.value)}
                sx={{ mb: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Registration Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <RegistrationIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Registration Details</Typography>
              </Box>

              <TextField
                fullWidth
                label="Company Registration Number"
                placeholder="e.g., 12345678"
                value={settings.registrationNumber || ''}
                onChange={(e) => handleChange('registrationNumber', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="VAT / Tax ID"
                placeholder="e.g., GB123456789"
                value={settings.taxId || ''}
                onChange={(e) => handleChange('taxId', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Country"
                placeholder="e.g., United Kingdom"
                value={settings.country || ''}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Registered Address */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AddressIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Registered Address</Typography>
              </Box>

              <TextField
                fullWidth
                label="Street Address"
                placeholder="123 Business Street"
                value={settings.registeredAddress?.street || ''}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="City"
                    value={settings.registeredAddress?.city || ''}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="State / Region"
                    value={settings.registeredAddress?.state || ''}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Postcode / ZIP"
                    value={settings.registeredAddress?.postcode || ''}
                    onChange={(e) => handleAddressChange('postcode', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={settings.registeredAddress?.country || ''}
                    onChange={(e) => handleAddressChange('country', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ContactIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Contact Information</Typography>
              </Box>

              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                placeholder="contact@company.com"
                value={settings.contactEmail || ''}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Contact Phone"
                placeholder="+44 20 1234 5678"
                value={settings.contactPhone || ''}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Settings */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CurrencyIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Financial Settings</Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Default Reporting Currency</InputLabel>
                    <Select
                      value={settings.defaultCurrency || 'USD'}
                      label="Default Reporting Currency"
                      onChange={(e) => handleChange('defaultCurrency', e.target.value)}
                    >
                      {currencies.map((currency) => (
                        <MenuItem key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={settings.timezone || 'UTC'}
                      label="Timezone"
                      onChange={(e) => handleChange('timezone', e.target.value)}
                    >
                      {timezones.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Fiscal Year Start</InputLabel>
                    <Select
                      value={settings.fiscalYearStart || 1}
                      label="Fiscal Year Start"
                      onChange={(e) => handleChange('fiscalYearStart', e.target.value)}
                    >
                      {MONTHS.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Inventory & COGS Source */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Inventory & COGS Source</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose where product costs (COGS) and stock levels are sourced from. This affects all brands in your company.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Inventory Source</InputLabel>
                    <Select
                      value={settings.inventorySource || 'internal'}
                      label="Inventory Source"
                      onChange={(e) => handleChange('inventorySource', e.target.value)}
                    >
                      <MenuItem value="internal">Internal Inventory System</MenuItem>
                      <MenuItem value="shopify">Shopify Inventory</MenuItem>
                      {(settings.availableInventorySources || []).includes('netsuite') && (
                        <MenuItem value="netsuite">NetSuite Inventory</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    {(() => {
                      const src = settings.inventorySource || 'internal';
                      if (src === 'netsuite') {
                        return (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>NetSuite Inventory</Typography>
                            <Typography variant="body2" color="text.secondary">
                              COGS and stock levels sourced from NetSuite via the connected TBA account. Multi-subsidiary
                              and multi-warehouse supported. Best when NetSuite is the system of record for inventory.
                            </Typography>
                          </>
                        );
                      }
                      if (src === 'shopify') {
                        return (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Shopify Inventory</Typography>
                            <Typography variant="body2" color="text.secondary">
                              COGS auto-synced from Shopify product costs (unitCost). Stock levels auto-synced from Shopify inventory locations.
                              Simplified inventory view — no warehouse management needed.
                            </Typography>
                          </>
                        );
                      }
                      return (
                        <>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Internal Inventory System</Typography>
                          <Typography variant="body2" color="text.secondary">
                            COGS from manual product costs entry. Stock levels from ShipStation, Amazon FBA, and manual uploads.
                            Full warehouse management with transfers, returns, and counts.
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, px: 4 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanySettingsTab;
